import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError, of } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ToastService } from './toast.service';
import { BroadcastChannelService } from './broadcast-channel.service';

interface LoginResponse {
  message: string;
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  mustResetPassword?: boolean;
  resetFlow?: string;
  twoFactorEnabled?: boolean;
  /** When to show "logout soon" modal (ms). From backend env JWT_EXPIRATION_WARNING_MS. */
  tokenExpirationWarningDuration?: number;
}

export interface TwoFactorSetupResponse {
  otpauthUrl: string;
  secret?: string;
}

export interface TwoFactorStatusResponse {
  message?: string;
  twoFactorEnabled: boolean;
  emailOtpEnabled?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private role: 'admin' | 'user' = 'user';
  private readonly AUTH_TOKEN_KEY = environment.auth.tokenKey;
  private readonly AUTH_USER_KEY = environment.auth.userKey;
  private readonly AUTH_EXPIRY_KEY = environment.auth.expiryKey;
  private readonly DEFAULT_TOKEN_TYPE = environment.auth.defaultTokenType || 'Bearer';
  private readonly MUST_RESET_PASSWORD_KEY = 'auth_must_reset_password';
  private readonly MUST_RESET_IDENTIFIER_KEY = 'auth_must_reset_identifier';
  private pendingResetPassword: string | null = null;
  private readonly apiBaseUrl = this.getApiBaseUrl();
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.hasToken());

  public isAuthenticated$: Observable<boolean> = this.isAuthenticatedSubject.asObservable();

  // Account Disabled Modal State
  private accountInactiveSubject = new BehaviorSubject<{ isOpen: boolean; date?: string }>({ isOpen: false });
  public accountInactive$ = this.accountInactiveSubject.asObservable();

  private broadcastChannelService = inject(BroadcastChannelService);

  constructor(
    private router: Router,
    private http: HttpClient,
    private toastService: ToastService,
  ) {
    // Listen to BroadcastChannel messages for cross-tab sync
    this.broadcastChannelService.messages$.subscribe(message => {
      switch (message.type) {
        case this.broadcastChannelService.MESSAGE_TYPES.ACCOUNT_DISABLED:
          this.handleAccountDisabled(message.data?.date);
          break;

        case this.broadcastChannelService.MESSAGE_TYPES.SESSION_REVOKED:
          this.clearAuthStorage();
          this.isAuthenticatedSubject.next(false);
          break;

        case this.broadcastChannelService.MESSAGE_TYPES.LOGOUT:
          this.clearAuthStorage();
          this.isAuthenticatedSubject.next(false);
          break;

        case this.broadcastChannelService.MESSAGE_TYPES.LOGIN:
        case this.broadcastChannelService.MESSAGE_TYPES.TOKEN_REFRESHED:
          // Token refreshed/logged in another tab - update auth state
          if (this.hasToken()) {
            this.isAuthenticatedSubject.next(true);
          }
          break;
      }
    });
  }

  /**
 * Normalize HttpErrorResponse to a user-friendly message.
 * Reused across the service to avoid duplication.
 */
  private extractErrorMessage(error: any, defaultMessage: string): string {
    let message = defaultMessage;
    if (!error) return message;

    if (error.status === 0) {
      message = 'Cannot reach the server. Please check your connection.';
    } else if (error.error?.message) {
      message = error.error.message;
    } else if (error.message) {
      message = error.message;
    }

    return message;
  }

  /**
   * Check if user has a valid token
   */
  private hasToken(): boolean {
    const token = localStorage.getItem(this.AUTH_TOKEN_KEY);
    if (!token) {
      return false;
    }

    const expiry = localStorage.getItem(this.AUTH_EXPIRY_KEY);
    if (expiry) {
      const expiresAt = Number(expiry);
      if (Number.isFinite(expiresAt) && Date.now() > expiresAt) {
        this.clearAuthStorage();
        return false;
      }
    }

    return true;
  }

  /**
   * Check if user is allowed use the feature
   * Note: Permission checks are now handled in the auth guard to avoid circular dependencies
   */
  private hasAccess(page: string): boolean {
    // Legacy hardcoded checks
    if (page === '/tenant') {
      return false;
    }

    // For routes that require permissions, the auth guard will handle the check
    // This method now only handles legacy/admin checks
    return true;
  }

  /**
   * Get current authentication status
   */
  isAuthenticated(): boolean {
    return this.hasToken();
  }

  /**
   * Get current accessible status
   */
  isAccessible(page: string): boolean {
    return this.hasAccess(page);
  }

  /**
   * Login user
   * @param emailOtpToken When provided, backend may verify this and issue tokens (for Email OTP flow).
   * @param options Optional flags (e.g. suppressSuccessToast when called from secondary flows like 2FA/Email OTP).
   */
  login(
    identifier: string,
    password: string,
    twoFactorToken?: string,
    emailOtpToken?: string,
    options?: { suppressSuccessToast?: boolean }
  ): Observable<LoginResponse> {
    const payload: any = { identifier: identifier.trim(), password };
    if (twoFactorToken) {
      payload.twoFactorToken = twoFactorToken;
    }
    if (emailOtpToken) {
      payload.emailOtpToken = emailOtpToken;
    }
    const url = this.apiBaseUrl ? `${this.apiBaseUrl}/api/auth/login` : '/api/auth/login';

    return this.http.post<LoginResponse>(url, payload).pipe(
      tap((response) => this.handleLoginSuccess(identifier, response, options)),
      catchError((error) => this.handleLoginError(error))
    );
  }

  /**
   * Send Email OTP (for login second step).
   * Backend expects identifier + password; it sends the code to the user's stored email.
   */
  sendEmailOtp(
    identifier: string,
    password: string,
    purpose: string = 'login'
  ): Observable<{ message: string; expiresInSeconds?: number; emailHint?: string }> {
    const url = this.apiBaseUrl ? `${this.apiBaseUrl}/api/auth/email-otp/send` : '/api/auth/email-otp/send';
    const body = { identifier: identifier.trim(), password, purpose };
    return this.http.post<{ message: string; expiresInSeconds?: number; emailHint?: string }>(url, body).pipe(
      catchError((err: HttpErrorResponse) => {
        const msg = err.error?.message || err.message || 'Failed to send verification code.';
        if (err.status === 429) {
          this.toastService.error(msg, undefined, 'Email OTP');
        }
        return throwError(() => new Error(msg));
      })
    );
  }

  /**
   * Verify Email OTP. Send identifier + password + otp (same as login credentials).
   */
  verifyEmailOtp(identifier: string, password: string, otp: string, purpose: string = 'login'): Observable<{ message: string; valid: boolean }> {
    const url = this.apiBaseUrl ? `${this.apiBaseUrl}/api/auth/email-otp/verify` : '/api/auth/email-otp/verify';
    const code = String(otp).replace(/\s/g, '').trim();
    const body = { identifier: identifier.trim(), password, otp: code, purpose };
    return this.http.post<{ message: string; valid: boolean }>(url, body).pipe(
      catchError((err: HttpErrorResponse) => {
        const msg = err.error?.message || err.message || 'Verification failed.';
        return throwError(() => new Error(msg));
      })
    );
  }

  /**
   * Register user
   */
  register(data: { email: string; username: string; name?: string; role?: string }) {
    const url = this.apiBaseUrl ? `${this.apiBaseUrl}/api/auth/register` : '/api/auth/register';
    return this.http.post<any>(url, data).pipe(
      tap((response) => this.handleRegisterSuccess(response)),
      catchError((error) => this.handleRegisterError(error))
    );
  }

  /**
   * Start the 2FA setup flow and retrieve the otpauth URL
   */
  startTwoFactorSetup(identifier: string, username: string, password: string): Observable<TwoFactorSetupResponse> {
    const url = this.apiBaseUrl ? `${this.apiBaseUrl}/api/auth/2fa/setup` : '/api/auth/2fa/setup';
    const headers = this.buildAuthHeaders();
    const payload = { identifier, username, password };

    return this.http.post<TwoFactorSetupResponse>(url, payload, { headers }).pipe(
      catchError((error) => this.handleTwoFactorError(error, 'Unable to start two-factor setup.'))
    );
  }

  /**
   * Post 2FA enabled status
   */
  getTwoFactorStatus(identifier: string): Observable<TwoFactorStatusResponse> {
    const url = this.apiBaseUrl ? `${this.apiBaseUrl}/api/auth/2fa/status` : '/api/auth/2fa/status';
    const headers = this.buildAuthHeaders();
    const payload = { identifier };

    return this.http.post<TwoFactorStatusResponse>(url, payload, { headers }).pipe(
      catchError((error) => this.handleTwoFactorError(error, 'Unable to load two-factor status.'))
    );
  }

  /**
   * Enable Email OTP for the current account (requires password).
   */
  enableEmailOtp(identifier: string, password: string): Observable<{ message: string }> {
    const url = this.apiBaseUrl ? `${this.apiBaseUrl}/api/auth/email-otp/enable` : '/api/auth/email-otp/enable';
    const headers = this.buildAuthHeaders();
    return this.http.post<{ message: string }>(url, { identifier: identifier.trim(), password }, { headers }).pipe(
      tap((response) => {
        const message = response?.message || 'Email OTP enabled.';
        this.toastService.success(message, undefined, 'Email OTP');
      }),
      catchError((error) => this.handleTwoFactorError(error, 'Unable to enable Email OTP.'))
    );
  }

  /**
   * Disable Email OTP for the current account (requires password).
   */
  disableEmailOtp(identifier: string, password: string): Observable<{ message: string }> {
    const url = this.apiBaseUrl ? `${this.apiBaseUrl}/api/auth/email-otp/disable` : '/api/auth/email-otp/disable';
    const headers = this.buildAuthHeaders();
    return this.http.post<{ message: string }>(url, { identifier: identifier.trim(), password }, { headers }).pipe(
      tap((response) => {
        const message = response?.message || 'Email OTP disabled.';
        this.toastService.success(message, undefined, 'Email OTP');
      }),
      catchError((error) => this.handleTwoFactorError(error, 'Unable to disable Email OTP.'))
    );
  }

  /**
   * Disable 2FA for the current account
   */
  disableTwoFactor(identifier: string, username: string, password: string): Observable<{ message: string }> {
    const url = this.apiBaseUrl ? `${this.apiBaseUrl}/api/auth/2fa/disable` : '/api/auth/2fa/disable';
    const headers = this.buildAuthHeaders();
    const payload = { identifier, username, password };

    return this.http.post<{ message: string }>(url, payload, { headers }).pipe(
      tap((response) => {
        const message = response?.message || 'Two-factor authentication disabled.';
        this.toastService.success(message, undefined, 'Two-Factor Authentication');
      }),
      catchError((error) => this.handleTwoFactorError(error, 'Unable to disable two-factor authentication.'))
    );
  }

  /**
   * Verify the OTP code to enable 2FA
   */
  verifyTwoFactorCode(identifier: string, username: string, password: string, code: string): Observable<LoginResponse> {
    const url = this.apiBaseUrl ? `${this.apiBaseUrl}/api/auth/2fa/verify` : '/api/auth/2fa/verify';
    const headers = this.buildAuthHeaders();
    const payload = { identifier, username, password, token: code };

    return this.http.post<LoginResponse>(url, payload, { headers }).pipe(
      tap((response) => {
        this.handleLoginSuccess(identifier, response);
      }),
      catchError((error) => this.handleTwoFactorError(error, 'Unable to verify the authentication code.'))
    );
  }

  /**
   * Request password reset (forgot password)
   * Sends a password reset email to the user
  */
  forgotPassword(email: string): Observable<{ message: string }> {
    const url = this.apiBaseUrl ? `${this.apiBaseUrl}/api/auth/forgot-password` : '/api/auth/forgot-password';
    const payload = { email };

    return this.http.post<{ message: string }>(url, payload).pipe(
      tap((response) => {
        const message = response?.message || 'Password reset email sent successfully.';
        this.toastService.success(message, undefined, 'Password Reset');
      }),
      catchError((error: HttpErrorResponse) => {
        const message = this.extractErrorMessage(error, 'Unable to send password reset email. Please try again.');
        this.toastService.error(message, undefined, 'Password Reset Failed');
        return throwError(() => new Error(message));
      })
    );
  }

  /**
   * Change user password
  */
  changePassword(
    identifier: string,
    currentPassword: string,
    newPassword: string,
    confirmPassword: string
  ): Observable<{ message: string }> {
    const url = this.apiBaseUrl ? `${this.apiBaseUrl}/api/auth/change-password` : '/api/auth/change-password';
    const headers = this.buildAuthHeaders();
    const payload = {
      identifier,
      password: currentPassword,
      newPassword,
      confirmPassword,
    };

    return this.http.post<{ message: string }>(url, payload, { headers }).pipe(
      tap((response) => {
        const message = response?.message || 'Password updated successfully.';
        this.toastService.success(message, undefined, 'Password Reset');
      }),
      catchError((error) => this.handleTwoFactorError(error, 'Unable to reset password.'))
    );
  }

  /**
   * Logout user
   */
  logout(): void {
    const url = this.apiBaseUrl ? `${this.apiBaseUrl}/api/auth/logout` : '/api/auth/logout';
    const headers = this.buildAuthHeaders();

    // Call API to revoke session (fire and forget or wait, but always clear local)
    if (headers) {
      this.http.post(url, {}, { headers }).subscribe({
        next: () => { },
        error: () => { },
        complete: () => this.finalizeLogout()
      });
    } else {
      this.finalizeLogout();
    }
  }

  private finalizeLogout() {
    this.clearAuthStorage();
    this.isAuthenticatedSubject.next(false);
    // Broadcast logout event to other tabs
    this.broadcastChannelService.postMessage(
      this.broadcastChannelService.MESSAGE_TYPES.LOGOUT
    );
    this.router.navigate(['/signin']);
  }

  /**
   * Refresh the current JWT token by getting a new token from the backend.
   * The new token will have the same tokenVersion but extended expiry (1 hour from now).
   */
  refreshToken(): Observable<LoginResponse> {
    const url = this.apiBaseUrl ? `${this.apiBaseUrl}/api/auth/refresh` : '/api/auth/refresh';
    const headers = this.buildAuthHeaders();

    if (!headers) {
      return throwError(() => new Error('No authentication token available'));
    }

    return this.http.post<LoginResponse>(url, {}, { headers }).pipe(
      tap((response) => {
        // Update token and expiry in storage
        const tokenPrefix = response.tokenType?.trim() || this.DEFAULT_TOKEN_TYPE;
        const token = response.accessToken ? `${tokenPrefix} ${response.accessToken}`.trim() : '';

        const tokenPayload = this.decodeAccessTokenPayload(response.accessToken);
        const expiresAt = this.resolveExpiry(response, tokenPayload);

        if (expiresAt) {
          localStorage.setItem(this.AUTH_EXPIRY_KEY, expiresAt.toString());
        }

        localStorage.setItem(this.AUTH_TOKEN_KEY, token);
        // Sync role from new token (isRoot is read from JWT when needed)
        const isRoot = tokenPayload?.isRoot === true;
        const roleFromString = (tokenPayload?.role as string)?.toLowerCase() === 'admin' ? 'admin' : 'user';
        this.setRole(isRoot ? 'admin' : roleFromString);
        this.isAuthenticatedSubject.next(true);
        if (typeof response.tokenExpirationWarningDuration === 'number' && response.tokenExpirationWarningDuration > 0) {
          localStorage.setItem('auth_warning_duration_ms', String(response.tokenExpirationWarningDuration));
        }
        // Broadcast token refresh event to other tabs
        // Note: TokenExpirationService will also broadcast, but this ensures AuthService state syncs
        this.broadcastChannelService.postMessage(
          this.broadcastChannelService.MESSAGE_TYPES.TOKEN_REFRESHED,
          { expiryTimestamp: expiresAt }
        );
      }),
      catchError((error: HttpErrorResponse) => {
        // Broadcast logout event to other tabs before logging out
        this.broadcastChannelService.postMessage(
          this.broadcastChannelService.MESSAGE_TYPES.LOGOUT
        );
        // If refresh fails, logout user
        this.logout();
        return throwError(() => new Error('Token refresh failed'));
      })
    );
  }

  showAccountInactiveModal(date?: string) {
    // Priority is set by interceptor, just show the modal
    this.accountInactiveSubject.next({ isOpen: true, date });
  }

  closeAccountInactiveModal() {
    this.accountInactiveSubject.next({ isOpen: false });
    this.finalizeLogout(); // Ensure user is logged out when acknowledging the modal
    // Priority will be cleared by interceptor or when modal closes naturally
  }

  /**
   * Get current user
   */
  getCurrentUser(): any {
    const userStr = localStorage.getItem(this.AUTH_USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Get auth token
   */
  getToken(): string | null {
    return localStorage.getItem(this.AUTH_TOKEN_KEY);
  }

  /**
   * Check if current user is a root/superadmin user.
   * Reads isRoot from the JWT token payload only (no localStorage).
   */
  isRoot(): boolean {
    const payload = this.getStoredTokenPayload();
    return payload?.isRoot === true;
  }

  /**
   * Set in-memory role (e.g. after login/refresh). Role is read from JWT via getRole().
   * @deprecated Use isRoot() instead for superadmin checks
   */
  setRole(role: 'admin' | 'user') {
    this.role = role;
  }


  private handleRegisterSuccess(response: any): void {
    const message = response?.message || 'Registration successful!';
    this.toastService.success(message, undefined, 'Registration Successful');
  }

  private handleRegisterError(error: HttpErrorResponse) {
    const message = this.extractErrorMessage(error, 'Unable to sign up. Please try again.');
    return throwError(() => new Error(message));
  }

  private handleLoginSuccess(
    email: string,
    response: LoginResponse,
    options?: { suppressSuccessToast?: boolean }
  ): void {
    // Only update token if a valid accessToken is provided
    if (!response.accessToken) {
      return;
    }

    const tokenPrefix = response.tokenType?.trim() || this.DEFAULT_TOKEN_TYPE;
    const token = `${tokenPrefix} ${response.accessToken}`.trim();

    const tokenPayload = this.decodeAccessTokenPayload(response.accessToken);
    if (!tokenPayload) {
      return;
    }

    const userEmail = (tokenPayload?.email as string) || email;
    const user = {
      email: userEmail,
      name: userEmail.split('@')[0],
      id: tokenPayload?.sub,
    };

    const expiresAt = this.resolveExpiry(response, tokenPayload);
    if (expiresAt) {
      localStorage.setItem(this.AUTH_EXPIRY_KEY, expiresAt.toString());
    }

    localStorage.setItem(this.AUTH_TOKEN_KEY, token);
    localStorage.setItem(this.AUTH_USER_KEY, JSON.stringify(user));

    // isRoot is read from JWT when needed via isRoot()
    const isRoot = tokenPayload?.isRoot === true;

    // Prefer JWT isRoot (boolean); fallback to role string or legacy heuristic
    const roleFromString = (tokenPayload?.role as string)?.toLowerCase() === 'admin' ? 'admin' : 'user';
    const inferredRole: 'admin' | 'user' = isRoot ? 'admin' : (roleFromString || (email.includes('admin') ? 'admin' : 'user'));
    this.setRole(inferredRole);

    this.isAuthenticatedSubject.next(true);

    if (typeof response.tokenExpirationWarningDuration === 'number' && response.tokenExpirationWarningDuration > 0) {
      localStorage.setItem('auth_warning_duration_ms', String(response.tokenExpirationWarningDuration));
    }

    if (response.mustResetPassword === true) {
      this.setPasswordResetRequired(userEmail);
    } else {
      this.clearPasswordResetRequired();
    }

    // Broadcast login event to other tabs
    this.broadcastChannelService.postMessage(
      this.broadcastChannelService.MESSAGE_TYPES.LOGIN
    );

    if (!options?.suppressSuccessToast) {
      this.toastService.success(response.message, undefined, 'Login successfully');
    }
  }

  public handleAccountDisabled(date?: string) {
    this.showAccountInactiveModal(date);
  }


  private handleLoginError(error: HttpErrorResponse) {
    let message = this.extractErrorMessage(error, 'Unable to sign in. Please try again.');

    if (error.status === 403 && error.error?.code === 'ACCOUNT_DISABLED') {
      this.handleAccountDisabled(error.error?.date || new Date().toISOString());
      return throwError(() => new Error('Account disabled'));
    }

    // 2FA required: no token issued yet. Caller should show 2FA prompt; do not toast "Login failed".
    if (error.status === 401 && error.error?.requires2fa === true) {
      const e = new Error(message) as Error & { requires2fa?: boolean };
      e.requires2fa = true;
      return throwError(() => e);
    }

    // Email OTP required: password valid, second step is email OTP. Caller should show Email OTP prompt.
    if (error.status === 401 && error.error?.requiresEmailOtp === true) {
      const e = new Error(message) as Error & { requiresEmailOtp?: boolean; emailHint?: string };
      e.requiresEmailOtp = true;
      e.emailHint = typeof error.error?.emailHint === 'string' ? error.error.emailHint : undefined;
      return throwError(() => e);
    }

    this.toastService.error(message, undefined, 'Login failed');
    return throwError(() => new Error(message));
  }

  /**
   * Clear all authentication-related data from storage
   * Public for use by session revocation and other services
   */
  clearAuthStorage(): void {
    localStorage.removeItem(this.AUTH_TOKEN_KEY);
    localStorage.removeItem(this.AUTH_USER_KEY);
    localStorage.removeItem(this.AUTH_EXPIRY_KEY);
    localStorage.removeItem('auth_role'); // Legacy key - clear if present from older versions
    localStorage.removeItem('auth_is_root'); // Legacy key - clear if present from older versions
    localStorage.removeItem('auth_warning_duration_ms');
    this.clearPasswordResetRequired();
  }

  isPasswordResetRequired(): boolean {
    return localStorage.getItem(this.MUST_RESET_PASSWORD_KEY) === 'true';
  }

  getPasswordResetIdentifier(): string | null {
    return localStorage.getItem(this.MUST_RESET_IDENTIFIER_KEY);
  }

  setPasswordResetRequired(identifier?: string): void {
    localStorage.setItem(this.MUST_RESET_PASSWORD_KEY, 'true');
    if (identifier?.trim()) {
      localStorage.setItem(this.MUST_RESET_IDENTIFIER_KEY, identifier.trim());
    }
  }

  clearPasswordResetRequired(): void {
    localStorage.removeItem(this.MUST_RESET_PASSWORD_KEY);
    localStorage.removeItem(this.MUST_RESET_IDENTIFIER_KEY);
    this.pendingResetPassword = null;
  }

  setPendingResetCredentials(identifier: string, password: string): void {
    const normalizedIdentifier = identifier.trim();
    if (!normalizedIdentifier || !password) return;
    this.setPasswordResetRequired(normalizedIdentifier);
    this.pendingResetPassword = password;
  }

  getPendingResetPassword(): string | null {
    return this.pendingResetPassword;
  }

  verifyFirstLoginToken(token: string): Observable<boolean> {
    const url = this.apiBaseUrl ? `${this.apiBaseUrl}/api/auth/first-login/verify` : '/api/auth/first-login/verify';
    return this.http.post<void>(url, { token }, { observe: 'response' as const }).pipe(
      map((response) => response.status === 204),
      catchError(() => of(false))
    );
  }

  private buildAuthHeaders(): HttpHeaders | undefined {
    const token = this.getToken();
    if (!token) {
      return undefined;
    }
    return new HttpHeaders({ Authorization: token });
  }

  private getApiBaseUrl(): string {
    if (environment.apiBaseUrl) {
      return environment.apiBaseUrl.trim().replace(/\/+$/, '');
    }

    const metaEnv = (import.meta as any)?.env?.NG_APP_API_BASE_URL;
    const globalEnv = (globalThis as any)?.__env?.baseUrl;
    const base = (metaEnv ?? globalEnv ?? '').toString().trim();
    return base.endsWith('/') ? base.slice(0, -1) : base;
  }

  /**
   * Get current user role. Reads from JWT token payload only (no localStorage).
   */
  getRole(): 'admin' | 'user' {
    const payload = this.getStoredTokenPayload();
    if (payload?.isRoot === true) {
      this.role = 'admin';
      return 'admin';
    }
    const payloadRole = (payload?.role as string) ?? '';
    if (payloadRole.toLowerCase() === 'admin') {
      this.role = 'admin';
      return 'admin';
    }
    this.role = 'user';
    return 'user';
  }

  getTenantId(): string | null {
    const payload = this.getStoredTokenPayload();
    const tenantId = payload?.tenantId;
    return tenantId ? String(tenantId) : null;
  }

  /**
   * Get the roleId from the JWT token payload
   * This is the MongoDB ObjectId of the user's assigned role
   */
  getRoleId(): string | null {
    const payload = this.getStoredTokenPayload();
    const roleId = payload?.roleId;
    return roleId ? String(roleId) : null;
  }

  private decodeAccessTokenPayload(rawToken: string | null): any | null {
    if (!rawToken) {
      return null;
    }

    const token = rawToken.includes(' ') ? rawToken.split(' ').pop() : rawToken;
    if (!token) {
      return null;
    }

    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }

    try {
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
      const jsonPayload = atob(padded);
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }

  private getStoredTokenPayload(): any | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }
    return this.decodeAccessTokenPayload(token);
  }

  private resolveExpiry(response: LoginResponse, tokenPayload: any | null): number | null {
    const expSeconds = tokenPayload?.exp;
    if (typeof expSeconds === 'number' && Number.isFinite(expSeconds)) {
      return expSeconds * 1000;
    }

    if (response.expiresIn) {
      return Date.now() + response.expiresIn * 1000;
    }

    return null;
  }

  private handleTwoFactorError(error: HttpErrorResponse, defaultMessage: string) {
    let message = this.extractErrorMessage(error, defaultMessage);
    let title = 'Two-Factor Authentication';

    const normalizedMessage = message.toLowerCase();
    const normalizedDefault = defaultMessage.toLowerCase();
    const isTwoFactorRequest = (error.url || '').includes('/api/auth/2fa');
    if (
      (normalizedMessage.includes('email/username') && normalizedMessage.includes('password')) ||
      (error.status === 401 && normalizedDefault.includes('two-factor')) ||
      (isTwoFactorRequest && normalizedMessage.includes('password'))
    ) {
      message = 'Invalid password.';
    }

    // Skip toast for SESSION_REVOKED errors - modal handles this
    if (error.error?.code === 'SESSION_REVOKED') {
      return throwError(() => new Error(message));
    }

    if (error.status === 401 || error.status === 404) {
      this.toastService.error(message, undefined);
    } else {
      this.toastService.error(message, undefined, title);
    }

    return throwError(() => new Error(message));
  }

  sendResetEmail(email: string): Observable<string> {
    const url = this.apiBaseUrl ? `${this.apiBaseUrl}/api/auth/forgot-password` : '/api/auth/forgot-password';
    return this.http.post<{ message: string }>(url, { email }).pipe(
      map((response) => {
        const message = response?.message || 'If an account with that email exists, a password reset email has been sent.';
        return message;
      }),
      catchError((error: HttpErrorResponse) => {
        const message = this.extractErrorMessage(error, 'Unable to send password reset email. Please try again later.');
        return throwError(() => new Error(message));
      })
    );
  }

  verifyResetToken(token: string): Observable<boolean> {
    const url = this.apiBaseUrl ? `${this.apiBaseUrl}/api/auth/reset-password/verify` : '/api/auth/reset-password/verify';

    return this.http.post<void>(url, { token }, { observe: 'response' as const }).pipe(
      map((response) => response.status === 204),
      catchError(() => of(false))
    );
  }

  resetPassword(token: string, newPassword: string, confirmPassword: string): Observable<{ message: string }> {
    const url = this.apiBaseUrl ? `${this.apiBaseUrl}/api/auth/reset-password` : '/api/auth/reset-password';
    const payload = { token, newPassword, confirmPassword };

    return this.http.post<{ message: string }>(url, payload).pipe(
      tap((response) => {
        const message = response?.message || 'Password reset successfully.';
        this.toastService.success(message, undefined, 'Password Reset');
      }),
      catchError((error: HttpErrorResponse) => {
        const message = this.extractErrorMessage(error, 'Unable to reset password. Please try again.');

        if (error.status === 400) {
          this.router.navigate(['/signin']);
        }

        this.toastService.error(message, undefined, 'Password Reset Failed');
        return throwError(() => new Error(message));
      })
    );
  }
}
